import React, { useMemo, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button,
  FormControlLabel, Radio, RadioGroup, TextField, Typography, IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { toHebrewNumeral } from '@NP-Webber/crm-kit';
import {
  countDafimInRanges,
  formatGemaraSelectionText,
  mergeContiguousRanges,
} from '../../../utils/gemaraSelection';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    color: '#f5f0e6',
    background: 'rgba(255,255,255,0.04)',
    borderRadius: 1.5,
    '& fieldset': { borderColor: 'rgba(212,175,55,0.3)' },
    '&:hover fieldset': { borderColor: 'rgba(212,175,55,0.55)' },
    '&.Mui-focused fieldset': { borderColor: '#d4af37' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(245,240,230,0.65)' },
};

function countDafim(ranges) {
  return countDafimInRanges(mergeContiguousRanges(ranges));
}

function formatPrevSelection(gemaraSelection) {
  return formatGemaraSelectionText(gemaraSelection, toHebrewNumeral) || 'אין פרטים';
}

const ExamsStep = ({
  tractates,
  activePeriod,
  previousRegistration,
  previousPeriod,
  currentRegistration,
  selectionMap,
  setSelectionMap,
  mergeMode,
  setMergeMode,
  onBack,
  onNext,
}) => {
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(false);

  const tractateNames = useMemo(() => Object.keys(tractates || {}), [tractates]);

  const totals = useMemo(() => {
    let exams = 0;
    let dafim = 0;
    for (const name of tractateNames) {
      const ranges = selectionMap[name];
      if (ranges?.length) {
        const c = countDafim(ranges);
        if (c > 0) {
          exams += 1;
          dafim += c;
        }
      }
    }
    return { exams, dafim };
  }, [selectionMap, tractateNames]);

  const ensureRanges = (tractate) => {
    setSelectionMap((prev) => {
      if (prev[tractate]?.length) return prev;
      return { ...prev, [tractate]: [{ from: '', to: '' }] };
    });
  };

  const updateRange = (tractate, idx, field, value) => {
    setSelectionMap((prev) => {
      const ranges = [...(prev[tractate] || [{ from: '', to: '' }])];
      ranges[idx] = { ...ranges[idx], [field]: value };
      return { ...prev, [tractate]: ranges };
    });
  };

  const addRange = (tractate) => {
    setSelectionMap((prev) => ({
      ...prev,
      [tractate]: [...(prev[tractate] || []), { from: '', to: '' }],
    }));
  };

  const removeRange = (tractate, idx) => {
    setSelectionMap((prev) => {
      const ranges = [...(prev[tractate] || [])];
      if (ranges.length <= 1) {
        const next = { ...prev };
        delete next[tractate];
        return next;
      }
      ranges.splice(idx, 1);
      return { ...prev, [tractate]: ranges };
    });
  };

  const clearTractate = (tractate) => {
    setSelectionMap((prev) => {
      const next = { ...prev };
      delete next[tractate];
      return next;
    });
  };

  const handleMergeModeChange = (e) => {
    setMergeMode(e.target.value);
    setSelectionMap({});
    setError('');
    setExpanded(false);
  };

  const handleNext = () => {
    setError('');
    const built = [];
    for (const name of tractateNames) {
      const ranges = selectionMap[name] || [];
      const valid = ranges
        .map((r) => ({ from: Number(r.from), to: Number(r.to) }))
        .filter((r) => Number.isInteger(r.from) && Number.isInteger(r.to));

      if (!ranges.some((r) => String(r.from).trim() || String(r.to).trim())) continue;

      if (!valid.length) {
        setError(`מסכת ${name}: יש למלא טווח דפים תקין`);
        return;
      }

      const maxDaf = tractates[name];
      for (const r of valid) {
        if (r.from < 2 || r.to < 2) {
          setError(`מסכת ${name}: מינימום דף הוא 2`);
          return;
        }
        if (r.from > r.to) {
          setError(`מסכת ${name}: דף התחלה גדול מדף הסיום`);
          return;
        }
        if (maxDaf != null && r.to > maxDaf) {
          setError(`מסכת ${name}: דף ${r.to} חורג מהמקסימום (${maxDaf})`);
          return;
        }
      }
      built.push({ tractate: name, ranges: valid });
    }

    if (!built.length) {
      setError('יש לבחור לפחות מסכת אחת עם טווח דפים');
      return;
    }

    onNext(built);
  };

  return (
    <Box sx={{ maxWidth: 640, mx: 'auto' }}>
      <Typography
        variant="h5"
        sx={{
          color: '#f5f0e6',
          fontFamily: '"Frank Ruhl Libre", "David Libre", serif',
          fontWeight: 700,
          mb: 1,
          textAlign: 'center',
        }}
      >
        סימון החומר
      </Typography>

      {activePeriod?.name && (
        <Typography sx={{ color: '#d4af37', textAlign: 'center', mb: 1, fontWeight: 600 }}>
          מועד: {activePeriod.name}
        </Typography>
      )}

      {!activePeriod && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          אין זמן מבחנים פעיל במערכת. פנו למשרד.
        </Alert>
      )}

      {currentRegistration && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          כבר נרשמת למועד זה. שמירה חדשה תחליף את ההרשמה הקיימת.
        </Alert>
      )}

      <Box
        sx={{
          mb: 2.5,
          p: 2,
          borderRadius: 2,
          border: '1px solid rgba(212,175,55,0.25)',
          background: 'rgba(10,77,60,0.2)',
        }}
      >
        <Typography sx={{ color: '#f5f0e6', fontWeight: 700, mb: 1, fontSize: '0.95rem' }}>
          איך מסמנים?
        </Typography>
        <Typography sx={{ color: 'rgba(245,240,230,0.75)', fontSize: '0.88rem', lineHeight: 1.7 }}>
          1. לחצו על שם המסכת כדי לפתוח.
          <br />
          2. הזינו מדף–עד דף בספרות בלבד (מינימום 2).
          <br />
          3. אפשר להוסיף כמה טווחים לאותה מסכת.
        </Typography>
      </Box>

      {previousRegistration?.gemaraSelection?.length > 0 && (
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={{ color: 'rgba(245,240,230,0.8)', mb: 1, fontSize: '0.9rem' }}>
            במבחן הקודם{previousPeriod?.name ? ` (${previousPeriod.name})` : ''}:{' '}
            <Box component="span" sx={{ color: '#e8c547' }}>
              {formatPrevSelection(previousRegistration.gemaraSelection)}
            </Box>
          </Typography>
          <RadioGroup
            value={mergeMode}
            onChange={handleMergeModeChange}
            sx={{ color: '#f5f0e6' }}
          >
            <FormControlLabel
              value="new"
              control={<Radio sx={{ color: '#d4af37', '&.Mui-checked': { color: '#d4af37' } }} />}
              label="הזנת חומר חדש"
            />
            <FormControlLabel
              value="merge"
              control={<Radio sx={{ color: '#d4af37', '&.Mui-checked': { color: '#d4af37' } }} />}
              label="הוספה על המבחן הקודם"
            />
          </RadioGroup>
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

      <Typography
        sx={{
          color: '#e8c547',
          fontWeight: 700,
          mb: 1.5,
          textAlign: 'center',
          fontSize: { xs: '0.9rem', sm: '0.95rem' },
          lineHeight: 1.6,
        }}
      >
        יש להזין מספרי דפים בספרות (לדוגמה: 2, 30) ולא באותיות (ב׳, ל׳)
      </Typography>

      <Box sx={{ mb: 2 }}>
        {tractateNames.map((name) => {
          const ranges = selectionMap[name];
          const selected = !!ranges?.length && countDafim(ranges) > 0;
          const hasDraft = !!ranges?.length;

          return (
            <Accordion
              key={name}
              expanded={expanded === name}
              onChange={(_, open) => {
                setExpanded(open ? name : false);
                if (open) ensureRanges(name);
              }}
              disableGutters
              sx={{
                mb: 1,
                borderRadius: '10px !important',
                background: selected
                  ? 'linear-gradient(90deg, rgba(10,77,60,0.55) 0%, rgba(10,77,60,0.25) 100%)'
                  : 'rgba(255,255,255,0.03)',
                border: selected
                  ? '1px solid rgba(46, 160, 110, 0.65)'
                  : '1px solid rgba(212,175,55,0.22)',
                color: '#f5f0e6',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#d4af37' }} />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1.5 }, width: '100%', pr: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, flex: 1, minWidth: 0 }}>{name}</Typography>
                  {selected && (
                    <Typography sx={{ color: '#7dcea0', fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {countDafim(ranges)} דף
                    </Typography>
                  )}
                  {hasDraft && (
                    <Button
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        clearTractate(name);
                      }}
                      sx={{ color: 'rgba(245,240,230,0.55)', minWidth: 0, fontSize: '0.75rem', flexShrink: 0 }}
                    >
                      נקה
                    </Button>
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography sx={{ color: 'rgba(245,240,230,0.55)', fontSize: '0.8rem', mb: 1.5 }}>
                  מקסימום דף: {tractates[name]} ({toHebrewNumeral(tractates[name])})
                </Typography>
                {(ranges || [{ from: '', to: '' }]).map((range, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 1,
                      alignItems: { xs: 'stretch', sm: 'center' },
                      mb: 1.2,
                    }}
                  >
                    <TextField
                      size="small"
                      label="מדף"
                      value={range.from}
                      onChange={(e) => updateRange(name, idx, 'from', e.target.value.replace(/\D/g, ''))}
                      inputProps={{ inputMode: 'numeric', dir: 'ltr' }}
                      sx={{ ...fieldSx, flex: 1 }}
                    />
                    <TextField
                      size="small"
                      label="עד דף"
                      value={range.to}
                      onChange={(e) => updateRange(name, idx, 'to', e.target.value.replace(/\D/g, ''))}
                      inputProps={{ inputMode: 'numeric', dir: 'ltr' }}
                      sx={{ ...fieldSx, flex: 1 }}
                    />
                    <IconButton
                      onClick={() => removeRange(name, idx)}
                      sx={{ color: 'rgba(245,240,230,0.5)', alignSelf: { xs: 'flex-end', sm: 'center' } }}
                      size="small"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => addRange(name)}
                  sx={{ color: '#d4af37', mt: 0.5 }}
                  size="small"
                >
                  הוסף טווח
                </Button>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: { xs: 1.5, sm: 3 },
          mb: 2.5,
          py: 1.5,
          borderTop: '1px solid rgba(212,175,55,0.2)',
          borderBottom: '1px solid rgba(212,175,55,0.2)',
        }}
      >
        <Typography sx={{ color: '#f5f0e6', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
          נבחרו: <Box component="strong" sx={{ color: '#e8c547' }}>{totals.exams}</Box> מסכתות
        </Typography>
        <Typography sx={{ color: '#f5f0e6', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
          <Box component="strong" sx={{ color: '#e8c547' }}>{totals.dafim}</Box> דף
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: 1.5 }}>
        <Button
          onClick={onBack}
          sx={{
            flex: 1,
            minHeight: 48,
            py: 1.2,
            borderRadius: 2,
            color: 'rgba(245,240,230,0.85)',
            border: '1px solid rgba(212,175,55,0.35)',
          }}
        >
          חזרה
        </Button>
        <Button
          onClick={handleNext}
          disabled={!activePeriod}
          sx={{
            flex: 2,
            minHeight: 48,
            py: 1.2,
            fontWeight: 700,
            borderRadius: 2,
            color: '#0a1f14',
            background: 'linear-gradient(135deg, #e8c547 0%, #d4af37 55%, #b8941f 100%)',
            '&:hover': { background: 'linear-gradient(135deg, #f0d060 0%, #e0bc45 55%, #c9a227 100%)' },
            '&.Mui-disabled': { background: 'rgba(212,175,55,0.25)', color: 'rgba(10,31,20,0.5)' },
          }}
        >
          המשך לסיכום
        </Button>
      </Box>
    </Box>
  );
};

export default ExamsStep;
